import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import API from '../../utils/api';
import { convertToINR } from '../../utils/currency';
import { FiPlus, FiDollarSign, FiShoppingBag, FiTrendingUp, FiEdit, FiTrash2, FiCheck, FiX } from 'react-icons/fi';

const SellerDashboard = () => {
  const [stats, setStats] = useState({
    totalBookings: 0,
    activeBookings: 0,
    completedBookings: 0,
    totalEarnings: 0,
    recentBookings: [],
  });
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState({});
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, productsRes] = await Promise.all([
        API.get('/bookings/stats/dashboard'),
        API.get('/products?limit=100'),
      ]);
      setStats(statsRes.data.stats);
      setProducts(productsRes.data.products);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ icon: Icon, label, value, color, bgColor }) => (
    <div className={`card p-6 ${bgColor}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-[rgba(34,34,34,0.7)] mb-1">{label}</p>
          <p className="text-3xl font-bold text-[#222222]">{value}</p>
        </div>
        <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${color} flex items-center justify-center shadow-lg`}>
          <Icon className="text-white text-2xl" />
        </div>
      </div>
    </div>
  );

  const handleDeleteProduct = async (productId) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        await API.delete(`/products/${productId}`);
        fetchDashboardData();
      } catch (error) {
        console.error('Error deleting product:', error);
        alert('Error deleting product');
      }
    }
  };

  const handleApproveBooking = async (bookingId) => {
    if (window.confirm('Approve this booking?')) {
      try {
        setActionLoading((prev) => ({ ...prev, [bookingId]: true }));
        setMessage({ type: '', text: '' });
        
        const response = await API.put(`/bookings/${bookingId}/status`, { status: 'Confirmed' });
        
        if (response.data.success) {
          setMessage({ type: 'success', text: 'Booking approved successfully!' });
          setTimeout(() => {
            fetchDashboardData();
            setMessage({ type: '', text: '' });
          }, 1500);
        }
      } catch (error) {
        console.error('Error approving booking:', error);
        const errorMsg = error.response?.data?.message || error.message || 'Error approving booking';
        setMessage({ type: 'error', text: errorMsg });
        alert(errorMsg);
      } finally {
        setActionLoading((prev) => ({ ...prev, [bookingId]: false }));
      }
    }
  };

  const handleRejectBooking = async (bookingId) => {
    if (window.confirm('Reject this booking?')) {
      try {
        setActionLoading((prev) => ({ ...prev, [bookingId]: true }));
        setMessage({ type: '', text: '' });
        
        const response = await API.put(`/bookings/${bookingId}/status`, { status: 'Rejected' });
        
        if (response.data.success) {
          setMessage({ type: 'success', text: 'Booking rejected successfully!' });
          setTimeout(() => {
            fetchDashboardData();
            setMessage({ type: '', text: '' });
          }, 1500);
        }
      } catch (error) {
        console.error('Error rejecting booking:', error);
        const errorMsg = error.response?.data?.message || error.message || 'Error rejecting booking';
        setMessage({ type: 'error', text: errorMsg });
        alert(errorMsg);
      } finally {
        setActionLoading((prev) => ({ ...prev, [bookingId]: false }));
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFF9D4] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[rgba(34,34,34,0.7)]">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFF9D4] py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl md:text-4xl font-display font-bold text-[#222222] mb-2">
              Seller Dashboard
            </h1>
            <p className="text-[rgba(34,34,34,0.7)]">Manage your products and bookings</p>
          </div>
          <Link
            to="/seller/products/new"
            className="btn-primary flex items-center space-x-2"
          >
            <FiPlus />
            <span>Add Product</span>
          </Link>
        </div>

        {/* Message Display */}
        {message.text && (
          <div className={`mb-6 p-4 rounded-lg ${
            message.type === 'success' 
              ? 'bg-green-100 border border-green-300 text-green-700' 
              : 'bg-red-100 border border-red-300 text-red-700'
          }`}>
            {message.text}
          </div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard
            icon={FiDollarSign}
            label="Total Earnings"
            value={`₹${convertToINR(stats.totalEarnings).toLocaleString('en-IN')}`}
            color="from-green-500 to-emerald-500"
            bgColor="bg-[#FFF5B8]"
          />
          <StatCard
            icon={FiShoppingBag}
            label="Total Bookings"
            value={stats.totalBookings}
            color="from-blue-500 to-cyan-500"
            bgColor="bg-[#FFF5B8]"
          />
          <StatCard
            icon={FiTrendingUp}
            label="Active Bookings"
            value={stats.activeBookings}
            color="from-purple-500 to-pink-500"
            bgColor="bg-[#FFF5B8]"
          />
          <StatCard
            icon={FiShoppingBag}
            label="Completed"
            value={stats.completedBookings}
            color="from-orange-500 to-red-500"
            bgColor="bg-[#FFF5B8]"
          />
        </div>

        {/* Recent Bookings */}
        <div className="card p-6 mb-8">
          <h2 className="text-xl font-semibold text-[#222222] mb-6">Recent Bookings</h2>
          
          {stats.recentBookings.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-[rgba(34,34,34,0.7)]">No bookings yet</p>
            </div>
          ) : (
            <>
              {/* Pending Bookings Section */}
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-[#222222] mb-3 flex items-center space-x-2">
                  <span className="w-3 h-3 bg-[#F27D16] rounded-full"></span>
                  <span>Pending Approval ({stats.recentBookings.filter(b => b.status === 'Pending').length})</span>
                </h3>
                {stats.recentBookings.filter(b => b.status === 'Pending').length === 0 ? (
                  <p className="text-[rgba(34,34,34,0.7)] text-center py-4">No pending bookings</p>
                ) : (
                  <div className="space-y-3">
                    {stats.recentBookings.filter(b => b.status === 'Pending').map((booking) => (
                      <div key={booking._id} className="flex items-center justify-between p-4 bg-[#FFF9D4] border-l-4 border-[#F27D16] rounded-lg hover:bg-[#FFF5B8] transition-colors">
                        <div className="flex items-center space-x-4 flex-1">
                          <img
                            src={booking.product?.images?.[0] || 'https://via.placeholder.com/60'}
                            alt={booking.product?.title}
                            className="w-12 h-12 rounded-lg object-cover"
                          />
                          <div className="flex-1">
                            <p className="font-medium text-[#222222]">{booking.product?.title}</p>
                            <p className="text-sm text-[rgba(34,34,34,0.7)]">by {booking.buyer?.name}</p>
                            <p className="text-xs text-[rgba(34,34,34,0.7)] mt-1">
                              {new Date(booking.startDate).toLocaleDateString()} - {new Date(booking.endDate).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-4">
                          <div className="text-right">
                            <p className="font-semibold text-[#222222]">₹{convertToINR(booking.totalAmount).toLocaleString('en-IN')}</p>
                            <span className="badge badge-warning">{booking.status}</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => handleApproveBooking(booking._id)}
                              disabled={actionLoading[booking._id]}
                              className="flex items-center space-x-1 px-3 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <FiCheck className="w-4 h-4" />
                              <span className="text-sm">Approve</span>
                            </button>
                            <button
                              onClick={() => handleRejectBooking(booking._id)}
                              disabled={actionLoading[booking._id]}
                              className="flex items-center space-x-1 px-3 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <FiX className="w-4 h-4" />
                              <span className="text-sm">Reject</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Confirmed Bookings Section */}
              <div className="border-t border-[#FCD06B] pt-8">
                <h3 className="text-lg font-semibold text-[#222222] mb-3 flex items-center space-x-2">
                  <span className="w-3 h-3 bg-[#386646] rounded-full"></span>
                  <span>Confirmed Bookings ({stats.recentBookings.filter(b => b.status === 'Confirmed').length})</span>
                </h3>
                {stats.recentBookings.filter(b => b.status === 'Confirmed').length === 0 ? (
                  <p className="text-[rgba(34,34,34,0.7)] text-center py-4">No confirmed bookings yet</p>
                ) : (
                  <div className="space-y-3">
                    {stats.recentBookings.filter(b => b.status === 'Confirmed').map((booking) => (
                      <div key={booking._id} className="flex items-center justify-between p-4 bg-[#FFF9D4] border-l-4 border-[#386646] rounded-lg hover:bg-[#FFF5B8] transition-colors">
                        <div className="flex items-center space-x-4 flex-1">
                          <img
                            src={booking.product?.images?.[0] || 'https://via.placeholder.com/60'}
                            alt={booking.product?.title}
                            className="w-12 h-12 rounded-lg object-cover"
                          />
                          <div className="flex-1">
                            <p className="font-medium text-[#222222]">{booking.product?.title}</p>
                            <p className="text-sm text-[rgba(34,34,34,0.7)]">by {booking.buyer?.name}</p>
                            <p className="text-xs text-[rgba(34,34,34,0.7)] mt-1">
                              {new Date(booking.startDate).toLocaleDateString()} - {new Date(booking.endDate).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-[#222222]">₹{convertToINR(booking.totalAmount).toLocaleString('en-IN')}</p>
                          <span className="badge badge-success flex items-center justify-center space-x-1">
                            <FiCheck className="w-3 h-3" />
                            <span>{booking.status}</span>
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* My Products */}
        <div className="card p-6">
          <h2 className="text-xl font-semibold text-[#222222] mb-4">My Products</h2>
          {products.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-[rgba(34,34,34,0.7)] mb-4">No products listed yet</p>
              <Link to="/seller/products/new" className="btn-primary">
                Add Your First Product
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {products.map((product) => (
                <div key={product._id} className="bg-[#FFF9D4] border border-[#FCD06B] rounded-lg overflow-hidden hover:shadow-md transition-shadow">
                  <img
                    src={product.images?.[0] || 'https://via.placeholder.com/300'}
                    alt={product.title}
                    className="w-full h-48 object-cover"
                  />
                  <div className="p-4">
                    <h3 className="font-semibold text-[#222222] mb-2 line-clamp-1">
                      {product.title}
                    </h3>
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-[#F27D16] font-semibold">
                        ₹{convertToINR(product.pricePerDay).toLocaleString('en-IN')}/day
                      </span>
                      <span className={`badge ${product.isAvailable ? 'badge-success' : 'badge-danger'}`}>
                        {product.isAvailable ? 'Available' : 'Unavailable'}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Link to={`/seller/products/${product._id}/edit`} className="flex-1 flex items-center justify-center space-x-1 px-3 py-2 bg-[#F27D16] text-white rounded-lg hover:bg-[#D96D0E] transition-colors">
                        <FiEdit />
                        <span>Edit</span>
                      </Link>
                      <button onClick={() => handleDeleteProduct(product._id)} className="px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors">
                        <FiTrash2 />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SellerDashboard;
